import React, { useState, useRef, useCallback } from 'react';
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Settings2,
  Moon,
  X
} from 'lucide-react';
import { useSleepTimer, formatSleepTime } from '../hooks/useSleepTimer';
import { PlayerMobileNav, PlayerSidebar } from './listen/PlayerNav';
import { useListenSession } from '../hooks/useListenSession';
import ListenSettingsForm from './listen/ListenSettingsForm';
import LiveLyricsPanel from './listen/LiveLyricsPanel';
import ListenVerseStage from './listen/ListenVerseStage';
import ChapterArtBackdrop from './listen/ChapterArtBackdrop';
import GitaLogo from './GitaLogo';
import { MOBILE_PLAYER_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import { PLAYBACK_MODES } from '../utils/buildPlaybackQueue';
import { formatTime } from '../utils/formatTime';

const Listen = () => {
  const {
    voice,
    setVoice,
    localSettings,
    updateLocal,
    handleStart,
    hasQueue,
    currentTrack,
    isPlaying,
    isLoading,
    error,
    stopPlayback,
    togglePlay,
    playNext,
    playPrevious,
    playNextChapter,
    playPreviousChapter,
    canPlayNextChapter,
    canPlayPreviousChapter,
    totalTracks,
    currentIndex,
    currentTime,
    duration,
    seek,
    speed,
    setSpeed
  } = useListenSession('apple');

  const { sleepMinutes, remainingSeconds, startTimer, cancelTimer } = useSleepTimer(stopPlayback);
  const [showSleepOptions, setShowSleepOptions] = useState(false);

  const [showSettings, setShowSettings] = useState(!hasQueue);
  const isMobileLayout = useMediaQuery(MOBILE_PLAYER_QUERY);

  const settingsPanelRef = useRef(null);
  const dragStartY = useRef(null);

  const handleSheetPointerDown = useCallback((e) => {
    dragStartY.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handleSheetPointerMove = useCallback((e) => {
    if (dragStartY.current === null) return;
    const dy = Math.max(0, e.clientY - dragStartY.current);
    if (settingsPanelRef.current) {
      settingsPanelRef.current.style.transform = `translateY(${dy}px)`;
      settingsPanelRef.current.style.transition = 'none';
    }
  }, []);

  const handleSheetPointerUp = useCallback((e) => {
    if (dragStartY.current === null) return;
    const dy = e.clientY - dragStartY.current;
    dragStartY.current = null;
    if (settingsPanelRef.current) {
      settingsPanelRef.current.style.transform = '';
      settingsPanelRef.current.style.transition = '';
    }
    if (dy > 80) setShowSettings(false);
  }, []);
  const modeLabel = PLAYBACK_MODES.find((m) => m.id === localSettings.mode)?.label || 'Verse';

  return (
    <div className="spotify-shell spotify-shell--fit">
      <PlayerMobileNav
        active="listen"
        showSettings={showSettings}
        onToggleSettings={() => setShowSettings((v) => !v)}
      />

      <PlayerSidebar active="listen">
        <div className="spotify-sidebar-playlist">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Session</p>
          <p className="text-sm text-white/70">
            Ch {localSettings.startChapter}–{localSettings.endChapter} · {modeLabel}
          </p>
          {hasQueue && (
            <p className="text-xs text-gita-gold mt-1">
              Track {currentIndex + 1} of {totalTracks}
            </p>
          )}
        </div>
      </PlayerSidebar>

      <div
        className={`spotify-main-wrap spotify-main-wrap--fit ${showSettings ? 'listen-main-wrap--settings-open' : ''}`}
      >
        <div className="listen-viewport">
          <header className="listen-viewport-header">
            <div className="listen-viewport-brand">
              <GitaLogo size={32} theme="dark" className="listen-viewport-logo shrink-0" />
              <div>
                <h1 className="listen-viewport-title">Continuous Listen</h1>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              className={`listen-viewport-tab ${showSettings ? 'active' : ''}`}
            >
              <Settings2 className="w-4 h-4" />
              Settings
            </button>
          </header>

          <div
            className={`listen-viewport-body ${showSettings ? 'listen-viewport-body--settings-open' : ''}`}
          >
        <ChapterArtBackdrop
          chapterNumber={currentTrack?.chapterNumber}
          slokaNumber={currentTrack?.slokaNumber}
          variant="maroon"
          className="listen-player-panel"
        >
          {currentTrack ? (
            <ListenVerseStage wideMeaning track={currentTrack}>
              <div className="listen-track-info listen-track-info--compact">
                <p className="listen-track-label">Now playing</p>
                <h2 className="listen-track-title">
                  Chapter {currentTrack.chapterNumber} · Verse {currentTrack.slokaNumber}
                </h2>
                <p className="listen-track-meta">
                  {currentTrack.type === 'meaning' ? 'Meaning narration' : 'Verse recitation'}
                  {currentTrack.speaker ? ` · ${currentTrack.speaker}` : ''}
                  {hasQueue && ` · Track ${currentIndex + 1} of ${totalTracks}`}
                </p>
              </div>

              <div className="listen-lyrics-slot">
                <LiveLyricsPanel
                  track={currentTrack}
                  currentTime={currentTime}
                  duration={duration}
                  seek={seek}
                  language={localSettings.language}
                  isPlaying={isPlaying}
                  layout={isMobileLayout ? 'compact' : 'compact-wide'}
                  useChapterBackdrop={false}
                  fitScreen
                  showWordBreakdown
                />
              </div>
            </ListenVerseStage>
          ) : (
            <div className="listen-idle listen-idle--fit">
              <p className="text-center mb-4">Configure your session, then start listening.</p>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="player-btn-primary-lg"
              >
                Open Settings
              </button>
            </div>
          )}

          <div className="listen-player-footer listen-player-footer--compact">
          <div className="listen-controls listen-controls--compact">
            <button
              type="button"
              onClick={playPreviousChapter}
              className="player-btn"
              aria-label="Previous chapter"
              disabled={!hasQueue || !canPlayPreviousChapter}
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={playPrevious} className="player-btn" aria-label="Previous track" disabled={!hasQueue}>
              <SkipBack className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={hasQueue ? togglePlay : handleStart}
              disabled={isLoading}
              className="player-btn-primary"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </button>
            <button type="button" onClick={playNext} className="player-btn" aria-label="Next track" disabled={!hasQueue}>
              <SkipForward className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={playNextChapter}
              className="player-btn"
              aria-label="Next chapter"
              disabled={!hasQueue || !canPlayNextChapter}
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
            {hasQueue && (
              <div className="listen-sleep-inline">
              {sleepMinutes ? (
                <button
                  type="button"
                  onClick={cancelTimer}
                  className="player-btn active flex items-center gap-1.5 text-xs"
                  title="Cancel sleep timer"
                >
                  <Moon className="w-4 h-4" />
                  {formatSleepTime(remainingSeconds)}
                </button>
              ) : showSleepOptions ? (
                <div className="flex items-center gap-1">
                  {[15, 30, 60].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { startTimer(m); setShowSleepOptions(false); }}
                      className="player-btn text-xs px-2"
                    >
                      {m}m
                    </button>
                  ))}
                  <button type="button" onClick={() => setShowSleepOptions(false)} className="player-btn">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSleepOptions(true)}
                  className="player-btn"
                  title="Sleep timer"
                >
                  <Moon className="w-4 h-4" />
                </button>
              )}
              </div>
            )}
          </div>

          {hasQueue && (
            <div className="listen-scrub-wrap">
              <div
                className="listen-scrub-bar"
                role="slider"
                aria-label="Track progress"
                aria-valuenow={duration > 0 ? Math.round((currentTime / duration) * 100) : 0}
                aria-valuemin={0}
                aria-valuemax={100}
                tabIndex={0}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  seek(((e.clientX - rect.left) / rect.width) * (duration || 0));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight') seek(Math.min(currentTime + 5, duration));
                  if (e.key === 'ArrowLeft') seek(Math.max(currentTime - 5, 0));
                }}
              >
                <div
                  className="listen-scrub-fill"
                  style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                />
                <div
                  className="listen-scrub-thumb"
                  style={{ left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>
              <div className="listen-scrub-times">
                <span>{formatTime(currentTime)}</span>
                <span>-{formatTime(Math.max(0, duration - currentTime))}</span>
              </div>
            </div>
          )}
          </div>
        </ChapterArtBackdrop>

        {showSettings && (
          <>
            <button
              type="button"
              className="listen-settings-scrim lg:hidden"
              aria-label="Close settings"
              onClick={() => setShowSettings(false)}
            />
            <aside className="listen-settings-panel" ref={settingsPanelRef}>
              <div
                className="sheet-drag-handle"
                aria-hidden="true"
                onPointerDown={handleSheetPointerDown}
                onPointerMove={handleSheetPointerMove}
                onPointerUp={handleSheetPointerUp}
                style={{ cursor: 'grab', touchAction: 'none' }}
              />
            <ListenSettingsForm
              localSettings={localSettings}
              updateLocal={updateLocal}
              voice={voice}
              setVoice={setVoice}
              handleStart={handleStart}
              stopPlayback={stopPlayback}
              hasQueue={hasQueue}
              isLoading={isLoading}
              error={error}
              speed={speed}
              setSpeed={setSpeed}
              variant="light"
            />
          </aside>
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Listen;
